import React from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-admin';
import { useBrand } from './ServerBrandProvider';
import { BrandLogo } from './BrandLogo';
import { useMenuItems } from '../hooks/useMenuItems';
import { useEnterpriseAdminMenuItems } from '../hooks/useEnterpriseAdminMenuItems';
import { isGolfOSVariant } from '../config/branding';
import { GolfOSMenu } from './golfos/GolfOSMenu';

// Define fixed menu width
const MENU_WIDTH = 240;

const CustomMenuItem = ({
  to,
  icon,
  text,
  isActive,
}: {
  to: string;
  icon: React.ReactNode;
  text: string;
  isActive: boolean;
}) => {
  const navigate = useNavigate();
  const { brand } = useBrand();

  // Default colors while branding loads
  const primaryColor = brand?.colors?.primary || '#1C5195';
  const secondaryColor = brand?.colors?.secondary || '#DDD8D6';

  return (
    <ListItemButton
      onClick={() => navigate(to)}
      sx={{
        borderLeft: '4px solid transparent',
        padding: '12px 16px',
        color: '#555',
        borderRadius: '0 12px 12px 0',
        minHeight: '48px',
        width: '100%',
        mx: 1,
        mb: 0.5,
        '&:hover': {
          borderLeft: `4px solid ${secondaryColor}`,
          color: primaryColor,
          backgroundColor: `${primaryColor}08`,
          transform: 'translateX(2px)',
          boxShadow: `0 2px 8px ${primaryColor}1A`,
        },
        ...(isActive && {
          borderLeft: `4px solid ${primaryColor}`,
          backgroundColor: `${primaryColor}1F`,
          color: primaryColor,
          fontWeight: 600,
          transform: 'translateX(4px)',
          boxShadow: `0 4px 12px ${primaryColor}26`,
        }),
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <ListItemIcon
        sx={{
          color: 'inherit',
          minWidth: '40px',
          transition: 'transform 0.2s ease',
          ...(isActive && {
            transform: 'scale(1.1)',
          }),
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={text}
        sx={{
          '& .MuiListItemText-primary': {
            color: 'inherit',
            fontWeight: 'inherit',
            fontSize: '0.875rem',
          },
        }}
      />
    </ListItemButton>
  );
};

const SectionHeader = ({ title, icon }: { title: string; icon?: React.ReactNode }) => {
  const { brand } = useBrand();
  const primaryColor = brand?.colors?.primary || '#1C5195';

  return (
    <Box sx={{ px: 2, py: 1.5, mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: primaryColor,
              fontSize: '0.875rem',
            }}
          >
            {icon}
          </Box>
        )}
        <Typography
          variant="caption"
          sx={{
            color: primaryColor,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontSize: '0.75rem',
          }}
        >
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

export const CustomMenu = () => {
  const location = useLocation();
  const { brand } = useBrand();
  const { identity } = useAuthState();
  const primaryColor = brand?.colors?.primary || '#1C5195';

  // Check if user is an enterprise admin (from stored user if identity not available)
  const storedUser = React.useMemo(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  }, []);

  const isEnterpriseAdmin =
    ((identity as any)?.isEnterpriseAdmin === true) ||
    (storedUser?.isEnterpriseAdmin === true);

  if (isGolfOSVariant && !isEnterpriseAdmin) {
    return <GolfOSMenu />;
  }

  // Get appropriate menu items based on user type
  const menuSections = isEnterpriseAdmin ? useEnterpriseAdminMenuItems() : useMenuItems();

  return (
    <Box
      className="dv-side-menu"
      sx={{
        textTransform: 'capitalize',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
        margin: '16px 0 16px 16px',
        borderRadius: '20px',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1200,
        height: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        width: MENU_WIDTH,
        border: `1px solid ${primaryColor}14`,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '24px 16px 20px 16px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          marginBottom: '16px',
          minHeight: '100px',
        }}
      >
        <BrandLogo
          width="90%"
          sx={{
            maxWidth: '90%',
            maxHeight: '120px',
            height: 'auto',
          }}
        />
      </Box>

      <Box sx={{ overflowY: 'auto', flex: 1, pb: 2 }}>
        {menuSections.map((section, sectionIndex) => (
          <Box key={section.title}>
            {sectionIndex === 0 ? null : (
              <SectionHeader title={section.title} icon={section.icon} />
            )}
            <List disablePadding sx={{ mb: sectionIndex === menuSections.length - 1 ? 0 : 1 }}>
              {section.items.map(item => (
                <CustomMenuItem
                  key={item.to}
                  to={item.to}
                  icon={item.icon}
                  text={item.text}
                  isActive={
                    location.pathname === item.path || location.pathname.startsWith(item.path + '/')
                  }
                />
              ))}
            </List>
            {sectionIndex < menuSections.length - 1 && section.title !== 'Dashboard' && (
              <Divider sx={{ mx: 2, my: 1.5, opacity: 0.2 }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
